import {Controller, useForm} from 'react-hook-form';
import {Panel, SelectInput, TextInput, Button} from '@sabre/spark-react-core';
import {MessageStatus} from '@sabre/spark-react-core/types';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import React, {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useI18Next} from '../../../../../common/helpers/react/hooks/useI18Next';
import ActionPanel from '../../../common/components/actionPanel/ActionPanel';
import {ButtonConfig} from '../../../../../controllers/dialog/ButtonConfig';
import {cssClasses} from '../../../../../common/helpers/browser/cssClasses';
import {useControllers} from '../../../../../common/helpers/react/hooks/useControllers';
import {useSubtitle} from '../../../../../common/helpers/react/hooks/useSubtitle';
import {tAsString} from '../../../../../common/helpers/react/text/tAsString';
import {asNumber} from '../../../../../common/helpers/converters/asNumber';
import {isDefined} from '../../../../../common/types/guards/isDefined';
import styles from './CreateOrUpdateConfiguration.module.css';
import {getStatusResponse} from "../../../../../common/helpers/json/getStatusResponse";
import {asString} from "../../../../../common/helpers/converters/asString";
import {ModalButtons} from "../../../../../controllers/dialog/ModalButtons";
import { renderDynamicField } from './configurationValueGUI/DynamicFieldRenderer';
import { useConfigurationSchema } from './configurationValueGUI/hooks/useConfigurationSchema';
import { useSchemaGUISync } from './configurationValueGUI/hooks/useSchemaGUISync';
import { FormModeToggle} from "./configurationValueGUI/FormModeToggle";

interface ConfigurationFormValues {
    redappName: string;
    configurationName: string;
    value: string;
    description: string;
    schemaGUI: Record<string, any>;
}

enum ActionType {
    UPDATE = 'UpdateConfiguration',
    CREATE = 'CreateConfiguration'
}

const Settings = {
    [ActionType.UPDATE]: {
        subtitle: 'EDIT_CONFIGURATION_HEADER',
        header: 'EDIT_CONFIGURATION_HEADER',
        successMessage: 'THE_X_CONFIGURATION_UPDATE_SUCCESS',
        errorMessage: 'THE_X_CONFIGURATION_UPDATE_FAILED'
    },
    [ActionType.CREATE]: {
        subtitle: 'NEW_CONFIGURATION',
        header: 'NEW_CONFIGURATION',
        successMessage: 'THE_X_CONFIGURATION_CREATION_SUCCESS',
        errorMessage: 'THE_X_CONFIGURATION_CREATION_FAILED'
    }
};

const getYupValidationSchema = (actionType: ActionType) => {
    return yup.object({
        redappName: yup
            .string()
            .required(tAsString('PROVIDE_A_FEATURE_NAME')),
        configurationName: yup
            .string()
            .required(tAsString('PROVIDE_A_CONFIGURATION_NAME'))
            .matches(/^[a-zA-Z0-9_]{1,32}$/, tAsString('ONLY_ALPHANUMERICS_AND_UNDERSCORES')),
        value: yup
            .string()
            .required(tAsString('PROVIDE_A_VALUE')),
        description: yup
            .string()
            .required(tAsString('PROVIDE_A_DESCRIPTION'))
    });
};

export const CreateOrUpdateConfiguration = () => {
    const {t} = useI18Next();
    const {id} = useParams();

    const [useFormMode, setUseFormMode] = React.useState<string>('json');
    const [allExpanded, setAllExpanded] = React.useState<boolean>(true);
    const [accordionKey, setAccordionKey] = React.useState<number>(0);

    const controllers = useControllers();
    const configurations = controllers.configurations;
    const redApps = controllers.redApps;
    const dialog = controllers.dialog;
    const http = controllers.http;

    const configurationId = asNumber(id);
    const configuration = configurations.getConfigurationById$(configurationId).useState();

    const actionType = isDefined(configuration) ? ActionType.UPDATE : ActionType.CREATE;
    const {subtitle, header, successMessage, errorMessage} = Settings[actionType];
    useSubtitle(tAsString(subtitle));

    const options = [
        {
            label: '-- Select --',
            value: ''
        }
    ];
    options.push(...(redApps.getRedApps$().useState() || []).map(redApp => ({
        value: redApp.iu_name,
        label: redApp.name
    })));

    const {
        control,
        formState: {errors},
        getValues,
        setValue,
        watch,
        handleSubmit
    } = useForm<ConfigurationFormValues>({
        mode: 'all',
        resolver: yupResolver(getYupValidationSchema(actionType))
    });


    const watchedRedappName = watch('redappName');

    const redappNameForSchema = actionType === ActionType.UPDATE
        ? configuration?.redapp_name
        : watchedRedappName;

    const { schemaObject, useFormModeActive } = useConfigurationSchema(
        redappNameForSchema,
        configurations
    );

    useSchemaGUISync(watch, setValue, schemaObject);

    const onSubmit = async () => {
        try {
            dialog.makeItBusy();
            const response = actionType === ActionType.UPDATE
                ? await configurations.updateConfiguration(configurationId, getValues('configurationName'), getValues('value'), getValues('description'))
                : await configurations.createConfiguration(getValues('redappName'), getValues('configurationName'), getValues('value'), getValues('description'));

                if (response.verificationStatus && response.verificationStatus.status === 'ERROR') {
                dialog.makeItIdle();
                const productName = options.find(it => it.value === getValues('redappName'))?.label;
                await dialog.showModal({
                    title: actionType === ActionType.CREATE
                        ? tAsString('NEW_CONFIGURATION')
                        : tAsString('EDIT_CONFIGURATION_HEADER'),
                    children: (<>
                        <p>
                            The configuration value does not match the application schema. You can
                            save this
                            but {productName} may not work properly with this configuration.
                            Technical
                            details:
                        </p>
                        <p style={{maxHeight: "50vh", overflowY: "auto"}}>
                            {response.verificationStatus.statusDescription}
                        </p>
                    </>),
                    buttons: [
                        {
                            secondary: true,
                            children: tAsString('SAVE_ANYWAY'),
                            className: 'qa-modal-save-anyway-button',
                            onClick: async () => {
                                await dialog.takeNotifiedAction({
                                    action: async () => {
                                        if (actionType === ActionType.UPDATE) {
                                            await configurations.updateConfiguration(configurationId, getValues('configurationName'), getValues('value'), getValues('description'), true);
                                        } else {
                                            await configurations.createConfiguration(getValues('redappName'), getValues('configurationName'), getValues('value'), getValues('description'), true);
                                        }
                                        await dialog.closeModal();
                                        await http.navigateTo('/configurations');
                                    },
                                    successMessage: t(successMessage, {
                                        name: getValues('configurationName')
                                    }),
                                    errorMessage: t(errorMessage, {
                                        name: getValues('configurationName')
                                    }),
                                    finalizer: async () => {
                                        await configurations.refreshConfigurations();
                                    }
                                });
                            }
                        },
                        ModalButtons.CANCEL(dialog, false),
                    ]
                });
            } else {
                const successNotificationMessage = t(successMessage, {
                    name: getValues('configurationName')
                });

                await dialog.showNotification({
                    title: tAsString('SUCCESSFUL'),
                    content: successNotificationMessage,
                    status: MessageStatus.SUCCESS,
                });
                await http.navigateTo('/configurations');
            }
        } catch (e: unknown) {

            const status = await getStatusResponse(e);

            const errorNotificationMessage = status.statusDescription
                || asString(e)
                || t(errorMessage, {
                    name: getValues('configurationName')
                })
                || tAsString('ERROR_RESULT');

            await dialog.showNotification({
                title: tAsString('ERROR'),
                content: errorNotificationMessage,
                status: MessageStatus.ERROR
            });
        } finally {
            await configurations.refreshConfigurations();
            dialog.makeItIdle();
        }
    };

    const onClose = async () => {
        await http.navigateTo('/configurations');
    };

    const actionButtons: ButtonConfig[] = [
        {
            className: cssClasses(
                'qa-cancel-button',
                'aat-autokey-escape'
            ),
            onClick: onClose,
            children: t('CANCEL'),
            secondary: true
        },
        {
            className: 'qa-save-and-close-button',
            children: t('SAVE_&_CLOSE'),
            onClick: handleSubmit(onSubmit)
        }
    ];

    return (
        <>
            <h1>{t(header)}</h1>
            <Panel>
                <form>
                    <div className={'row'} style={{justifyContent: 'center'}}>
                        <div className={'col-lg-6'}>
                            <Controller
                                name={'redappName'}
                                control={control}
                                defaultValue={configuration?.redapp_name}
                                render={({field}) => {
                                    const commonProps = {
                                        name: field.name,
                                        defaultValue: '-',
                                        onChange: field.onChange,
                                        onBlur: field.onBlur,
                                        status: errors[field.name] ? MessageStatus.ERROR : undefined,
                                        statusMessage: errors[field.name]?.message,
                                        label: t('FEATURE_NAME'),
                                        className: 'qa-configuration-redapp-name-input'
                                    }

                                    return actionType === ActionType.CREATE
                                        ? <SelectInput
                                            {...commonProps}
                                            value={field.value}
                                            onChange= {(value) => {
                                                field.onChange(value);
                                                setValue('value', '')
                                            }}
                                            disabled={false}
                                            options={options}
                                        />
                                        : <TextInput
                                            {...commonProps}
                                            value={options.find(it => it.value === field.value)?.label ?? field.value}
                                            disabled={actionType === ActionType.UPDATE}
                                        />;
                                }}
                            />
                        </div>
                        <div className={'col-lg-6 spark-pad-t-1--lte-md'}>
                            <Controller
                                name={'configurationName'}
                                control={control}
                                defaultValue={configuration?.configuration_name}
                                render={({field}) =>
                                    <TextInput
                                        name={field.name}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        status={errors[field.name] ? MessageStatus.ERROR : undefined}
                                        statusMessage={errors[field.name]?.message}
                                        label={t('CONFIGURATION_NAME')}
                                        className={'qa-configuration-name-input'}
                                        maxLength={32}
                                    />
                                }
                            />
                        </div>
                    </div>
                    <div className={'row'} style={{justifyContent: 'center'}}>
                        <div className={'col-lg-12 spark-pad-t-1'}>
                            <Controller
                                name={'description'}
                                control={control}
                                defaultValue={configuration?.description}
                                render={({field}) =>
                                    <TextInput
                                        name={field.name}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        status={errors[field.name] ? MessageStatus.ERROR : undefined}
                                        statusMessage={errors[field.name]?.message}
                                        label={t('DESCRIPTION')}
                                        className={'qa-configuration-description-input'}
                                        maxLength={1000}
                                    />
                                }
                            />
                        </div>

                        <div className={'col-lg-12 spark-pad-t-1'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                            <FormModeToggle
                                onChange={setUseFormMode}
                            />
                            {useFormMode === 'form' && useFormModeActive && schemaObject && (
                                <div style={{ display: 'flex', gap: '8px', position: 'absolute', right: '1rem' }}>
                                    <Button
                                        type="button"
                                        size="sm"
                                        secondary
                                        className="qa-configuration-collapse-all-button"
                                        onClick={() => {
                                            setAllExpanded(false);
                                            setAccordionKey(prev => prev + 1);
                                        }}
                                    >
                                        Collapse All
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        secondary
                                        className="qa-configuration-expand-all-button"
                                        onClick={() => {
                                            setAllExpanded(true);
                                            setAccordionKey(prev => prev + 1);
                                        }}
                                    >
                                        Expand All
                                    </Button>
                                </div>
                            )}
                        </div>

                        {useFormMode === 'json' ? (
                            <div className={'col-lg-12 spark-pad-t-1'}>
                                <Controller
                                    name={'value'}
                                    control={control}
                                    defaultValue={configuration?.value}
                                    render={({field}) =>
                                        <TextInput
                                            name={field.name}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            status={errors[field.name] ? MessageStatus.ERROR : undefined}
                                            statusMessage={errors[field.name]?.message}
                                            label={t('VALUE')}
                                            className={cssClasses(
                                                'qa-configuration-value-input',
                                                styles.TextArea
                                            )}
                                            multiLine={true}
                                            maxLength={1048576}
                                        />
                                    }
                                />
                            </div>
                        ) : (
                            <div className={'col-lg-12 spark-pad-t-1'}>
                                {useFormModeActive && schemaObject ? (
                                    <div key={accordionKey} className="qa-configuration-dynamic-form-container">
                                        {schemaObject?.properties && Object.entries(schemaObject.properties).map(([key, prop]: [string, any]) => (
                                            <div key={key} className="spark-form-group qa-configuration-dynamic-form">
                                                {renderDynamicField({
                                                    property: key,
                                                    propertySchema: prop,
                                                    parentPath: 'schemaGUI',
                                                    control,
                                                    watch,
                                                    setValue,
                                                    schemaObject,
                                                    allExpanded
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ fontFamily: '"Roboto Condensed", sans-serif' }} className={'qa-configuration-no-schema'}>
                                        {t('SCHEMA_NOT_AVAILABLE_MESSAGE')}
                                    </p>
                                )}
                            </div>
                            )}
                    </div>
                </form>
            </Panel>
            <ActionPanel buttons={actionButtons}/>
        </>
    );
};